import { Loader2, Search, TrendingUp } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  adminClient,
  type DashboardMetricsResponse,
  type DashboardOrgMetrics,
  type DashboardTrendsResponse,
} from '@/api/adminClient';
import {
  BuildsTrendChart,
  OrganizationsTrendChart,
  SeriesKey,
  UsageTrendChart,
  trendDelta,
} from '@/components/dashboard/DashboardTrends';
import { NeedsAttention } from '@/components/home/NeedsAttention';
import { BillingCell } from '@/components/org/BillingCell';
import { AdminPageHeader, PAGE_CONTAINER_CLASS } from '@/components/shell/AdminPageHeader';
import { TablePager } from '@/components/table/TablePager';
import { useAdminAccess } from '@/hooks/useAdminAccess';
import { Button } from '@/components/ui/button';
import { LoadError } from '@/components/ui/load-state';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { formatCount, formatUsd, orgLabel, rangeForDays } from '@/lib/format';
import { cn } from '@/lib/utils';

const PERIODS = [7, 30, 90] as const;
type Period = (typeof PERIODS)[number];

const PAGE_SIZE = 25;

function MetricCard({
  label,
  value,
  subValue,
  delta,
  period,
  chart,
}: {
  label: string;
  value: string;
  subValue?: ReactNode;
  delta?: ReturnType<typeof trendDelta>;
  period: number;
  /** A small chart of the same number, day by day. */
  chart?: ReactNode;
}) {
  return (
    <div className="flex flex-col rounded-xl border border-border bg-card p-4">
      <p className="text-xs font-medium text-muted-foreground">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <p className="text-xl font-semibold tabular-nums">{value}</p>
        {delta ? (
          <span
            title={`Compared with the previous ${period} days`}
            className={cn(
              'rounded-full px-1.5 py-0.5 text-[11px] font-medium tabular-nums',
              delta.direction === 'up' && 'bg-success-bg text-success-foreground',
              delta.direction === 'down' && 'bg-destructive-bg text-destructive-foreground',
              delta.direction === 'flat' && 'bg-muted text-muted-foreground',
            )}
          >
            {delta.label}
          </span>
        ) : null}
      </div>
      {subValue ? (
        <div className="mt-0.5 text-xs text-muted-foreground">{subValue}</div>
      ) : null}
      {chart ? <div className="mt-auto">{chart}</div> : null}
    </div>
  );
}

function TopIntegrationsList({
  integrations,
}: {
  integrations: DashboardMetricsResponse['topIntegrations'];
}) {
  if (integrations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No integration usage in this period</p>
    );
  }

  return (
    <div className="space-y-2">
      {integrations.slice(0, 5).map((integration) => (
        <div
          key={integration.integrationId}
          className="flex items-center justify-between text-sm"
        >
          <span className="truncate font-medium">{integration.integrationId}</span>
          <span className="tabular-nums text-muted-foreground">
            {formatUsd(integration.totalCostUsd)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function SalesMetricsDashboardPage() {
  const { isPlatformAdmin } = useAdminAccess();
  const [days, setDays] = useState<Period>(30);
  const [data, setData] = useState<DashboardMetricsResponse | undefined>();
  const [trends, setTrends] = useState<DashboardTrendsResponse | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string | undefined>();
  const [page, setPage] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(undefined);
      try {
        const range = rangeForDays(days);
        const [result, trendResult] = await Promise.all([
          adminClient.getDashboardMetrics({
            ...range,
            orgId: selectedOrgId,
            includeZeroMetrics: Boolean(selectedOrgId),
          }),
          // Trends are extra: the dashboard still loads if they fail.
          adminClient.getDashboardTrends({ ...range, orgId: selectedOrgId }).catch(() => undefined),
        ]);
        if (!cancelled) {
          setData(result);
          setTrends(trendResult);
          setPage(0);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load dashboard metrics');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days, selectedOrgId, reloadKey]);

  const filteredOrgs = useMemo(() => {
    if (!data) return [];
    const needle = searchQuery.trim().toLowerCase();
    if (!needle) return data.organizations;
    return data.organizations.filter((org) =>
      [org.displayName, org.name, org.organizationId]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    );
  }, [data, searchQuery]);

  const pageCount = Math.max(1, Math.ceil(filteredOrgs.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageOrgs = filteredOrgs.slice(
    safePage * PAGE_SIZE,
    (safePage + 1) * PAGE_SIZE,
  );

  const orgOptions = useMemo(() => {
    if (!data) return [];
    return data.organizations.map((org) => ({
      value: org.organizationId,
      label: orgLabel(org),
    }));
  }, [data]);

  const handleOrgFilterChange = (value: string) => {
    if (value === '__all__') {
      setSelectedOrgId(undefined);
    } else {
      setSelectedOrgId(value);
    }
    setSearchQuery('');
  };

  const clearOrgFilter = () => {
    setSelectedOrgId(undefined);
    setSearchQuery('');
  };

  return (
    <div className={PAGE_CONTAINER_CLASS}>
      <AdminPageHeader
        title="Home"
        description={`What needs you, then platform-wide usage and billing metrics for the last ${days} days, compared with the ${days} days before. Organizations with zero activity are hidden by default.`}
        actions={
          <div className="flex gap-1">
            {PERIODS.map((period) => (
              <Button
                key={period}
                type="button"
                size="sm"
                variant={days === period ? 'default' : 'outline'}
                onClick={() => setDays(period)}
              >
                {period}d
              </Button>
            ))}
          </div>
        }
      />

      {isPlatformAdmin ? <NeedsAttention /> : null}

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading metrics
        </div>
      ) : error ? (
        <LoadError
          message={error}
          onRetry={() => setReloadKey((key) => key + 1)}
          className="rounded-xl border border-border bg-card"
        />
      ) : data ? (
        <div className="space-y-6">
          {/* One row: each number with its change and a small chart, so the organizations stay in view. */}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              label="Total usage"
              value={formatUsd(data.summary.totalCostUsd)}
              subValue={
                <SeriesKey
                  items={[
                    { label: `LLM ${formatUsd(data.summary.totalLlmCostUsd)}`, colorIndex: 0 },
                    { label: `Integrations ${formatUsd(data.summary.totalIntegrationCostUsd)}`, colorIndex: 2 },
                  ]}
                />
              }
              delta={trends ? trendDelta(trends.current.totalCostUsd, trends.previous.totalCostUsd) : undefined}
              period={days}
              chart={trends ? <UsageTrendChart days={trends.days} /> : undefined}
            />
            <MetricCard
              label="Builds"
              value={formatCount(data.summary.totalRuns)}
              subValue={`about ${formatCount(Math.round(data.summary.totalRuns / days))} a day`}
              delta={trends ? trendDelta(trends.current.runs, trends.previous.runs) : undefined}
              period={days}
              chart={trends ? <BuildsTrendChart days={trends.days} /> : undefined}
            />
            <MetricCard
              label="Active organizations"
              value={formatCount(data.summary.activeOrgsWithUsage)}
              subValue={
                <SeriesKey
                  items={[
                    { label: `Active, of ${formatCount(data.summary.totalOrgs)}`, colorIndex: 0 },
                    ...(trends ? [{ label: `${formatCount(trends.current.newApps)} new apps`, colorIndex: 3 }] : []),
                  ]}
                />
              }
              delta={trends ? trendDelta(trends.current.activeOrgs, trends.previous.activeOrgs) : undefined}
              period={days}
              chart={trends ? <OrganizationsTrendChart days={trends.days} /> : undefined}
            />
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="mb-2 flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                <p className="text-xs font-medium text-muted-foreground">Top integrations</p>
              </div>
              <TopIntegrationsList integrations={data.topIntegrations} />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative w-full max-w-xs">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
                <Input
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setPage(0);
                  }}
                  placeholder="Search organizations..."
                  aria-label="Search organizations"
                  className="pl-9"
                />
              </div>
              <Select
                value={selectedOrgId || '__all__'}
                onValueChange={handleOrgFilterChange}
              >
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Filter by organization" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All organizations</SelectItem>
                  {orgOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedOrgId ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearOrgFilter}
                >
                  Clear filter
                </Button>
              ) : null}
              <p className="text-xs text-muted-foreground">
                {formatCount(filteredOrgs.length)} org
                {filteredOrgs.length === 1 ? '' : 's'}
                {filteredOrgs.length !== data.organizations.length
                  ? ` (of ${formatCount(data.organizations.length)})`
                  : ''}
              </p>
            </div>

            {filteredOrgs.length === 0 && !selectedOrgId ? (
              <div className="rounded-xl border border-border p-8 text-center">
                <p className="text-sm text-muted-foreground">
                  No organizations have usage metrics in the last {days} days.
                </p>
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-border">
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="border-b border-border bg-muted/40 text-xs font-medium text-muted-foreground">
                      <tr>
                        <th className="px-4 py-3">Organization</th>
                        <th className="px-4 py-3">Usage (total)</th>
                        <th className="px-4 py-3">LLM</th>
                        <th className="px-4 py-3">Integrations</th>
                        <th className="px-4 py-3">Builds</th>
                        <th className="px-4 py-3">Billing status</th>
                        <th className="px-4 py-3">Credits</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pageOrgs.length === 0 ? (
                        <tr>
                          <td
                            colSpan={7}
                            className="px-4 py-10 text-center text-muted-foreground"
                          >
                            No organizations match your search
                          </td>
                        </tr>
                      ) : (
                        pageOrgs.map((org) => (
                          <OrgRow key={org.organizationId} org={org} />
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                <TablePager
                  total={filteredOrgs.length}
                  limit={PAGE_SIZE}
                  offset={safePage * PAGE_SIZE}
                  onOffsetChange={(next) => setPage(Math.floor(next / PAGE_SIZE))}
                />
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function OrgRow({ org }: { org: DashboardOrgMetrics }) {
  return (
    <tr className="border-b border-border last:border-0 hover:bg-muted/40">
      <td className="px-4 py-3">
        <Link
          to={`/organizations/${encodeURIComponent(org.organizationId)}`}
          className="font-medium text-foreground hover:underline"
        >
          {orgLabel(org)}
        </Link>
        {org.name && org.name !== org.displayName ? (
          <p className="text-xs text-muted-foreground">{org.name}</p>
        ) : null}
      </td>
      <td className="px-4 py-3 font-medium tabular-nums">
        {formatUsd(org.usage.totalCostUsd)}
      </td>
      <td className="px-4 py-3 tabular-nums text-muted-foreground">
        {formatUsd(org.usage.llmCostUsd)}
      </td>
      <td className="px-4 py-3 tabular-nums text-muted-foreground">
        {formatUsd(org.usage.integrationCostUsd)}
      </td>
      <td className="px-4 py-3 tabular-nums">{formatCount(org.usage.runs)}</td>
      <td className="px-4 py-3">
        <BillingCell productName={org.billing.productName} status={org.billing.status} />
      </td>
      <td className="px-4 py-3 tabular-nums">
        {typeof org.billing.creditBalanceUsd === 'number'
          ? formatUsd(org.billing.creditBalanceUsd)
          : '—'}
      </td>
    </tr>
  );
}
